import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, ChevronRight, CircleHelp, Copy, FileText, FlaskConical, RefreshCw, Sparkles, X, XCircle } from 'lucide-react';
import { useToast } from '../../../../components/common/Toast';
import { DialogShell, fieldClass, ghostBtn, iconBtn, labelClass, primaryBtn, surfaces } from '../components/dataModelsUi';
import { detailStatusFromList, launchScope, listStatusFromDetail, useGovernance, type GovernanceLevel, type GovernancePolicyRecord, type PolicyStatus } from '../components/governanceStore';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, ModelTable, Space } from '../dataModelsTypes';
import { assetsInScope } from './catalog';
import { resolveDirectoryUserId } from './policyDirectory';
import { usePolicyDirectory } from './usePolicyDirectory';
import { defaultRule, emptyPolicy, emptySubject, policyDefinitionMode, validatePolicyDraft } from './engine';
import { assetsForRowFilter, usePolicyAssetColumns } from './usePolicyAssetColumns';
import ExpressionHelpDialog from './ExpressionHelpDialog';
import PolicyTestDialog from './PolicyTestDialog';
import { AppliesToSection, PolicyDefinitionFields, Section } from './PolicyFormSections';
import { currentPolicyExpression, testExpressionQuery, userById, type QueryTestResult } from './queryTest';
import { suggestRules, suggestionKey, type RuleSuggestion } from './aiSuggestions';
import { definitionMode, formatPolicyJson, toPolicyDocument } from './policyInspect';
import { buildPolicyPath, newPolicyUuid, slugify } from './policyPath';
import type { PolicyEditorMode } from './policyEditorNav';
import type { GovernancePolicy, PolicyType } from './types';
import { KIND_POLICY_TYPE, POLICY_DOCUMENT_SCHEMA, POLICY_TYPE_LABEL } from './types';

const EDITOR_TYPES: PolicyType[] = ['ROW_LEVEL', 'COLUMN_LEVEL', 'ACCESS_CONTROL', 'SENSITIVE_DATA'];
const ACTIVATION: PolicyStatus[] = ['Draft', 'Enabled', 'Disabled'];

function accessSeed(): GovernancePolicy['rules'] {
  return [
    { id: 'rule-analyst', subject: { ...emptySubject(), mode: 'roles', roles: ['analyst'] }, action: { type: 'ACCESS', effect: 'deny', operations: ['SELECT'] } },
    { id: 'rule-everyone', subject: emptySubject(), action: { type: 'ACCESS', effect: 'allow', operations: ['SELECT'] } },
  ];
}

function fromRecord(record: ReturnType<typeof useGovernance>['policies'][number], scope: GovernancePolicy['scope']): GovernancePolicy {
  if (record.detail) {
    return {
      ...record.detail,
      policyKey: record.detail.policyKey || slugify(record.detail.name || record.name),
      generateId: Boolean(record.detail.generateId || record.detail.document?.meta?.generatedId),
      status: record.detail.status || detailStatusFromList(record.status),
      document: record.detail.document,
    };
  }
  return {
    ...emptyPolicy(scope, KIND_POLICY_TYPE[record.kind] ?? 'ROW_LEVEL'),
    id: record.id,
    name: record.name,
    description: record.description,
    status: detailStatusFromList(record.status),
    policyKey: slugify(record.name),
    generateId: false,
  };
}

export default function PolicyEditorPage({
  isLight,
  mode,
  level,
  policyId,
  space,
  model,
  table,
  onClose,
}: {
  isLight: boolean;
  mode: PolicyEditorMode;
  level: GovernanceLevel;
  policyId?: string;
  space: Space;
  model?: DataModel;
  table?: ModelTable;
  onClose: () => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const { showToast } = useToast();
  const { policies, assets, createPolicyDraft, updatePolicyDraft, activatePolicy, deactivatePolicy, isPolicyKeyTaken } = useGovernance();
  const scope = launchScope(level, space.id, model?.id, table?.id);
  const scopedAssets = useMemo(() => assetsInScope(assets, scope), [assets, scope]);
  const { assets: assetsWithColumns, loading: columnsLoading } = usePolicyAssetColumns(scopedAssets);
  const existing = policyId ? policies.find((item) => item.id === policyId) : undefined;
  const [editorMode, setEditorMode] = useState<PolicyEditorMode>(mode);
  const [complexity, setComplexity] = useState<'simple' | 'complex'>(() => definitionMode(existing?.detail?.document));
  const [keyDirty, setKeyDirty] = useState(Boolean(existing?.detail?.generateId === false && existing?.detail?.policyKey));
  const [errors, setErrors] = useState<string[]>([]);
  const [askOpen, setAskOpen] = useState(false);
  const [askAnalyzing, setAskAnalyzing] = useState(false);
  const [askAnalysis, setAskAnalysis] = useState('');
  const [askSuggestions, setAskSuggestions] = useState<RuleSuggestion[]>([]);
  const directory = usePolicyDirectory();
  const [queryUserId, setQueryUserId] = useState('');
  const [queryTestResult, setQueryTestResult] = useState<QueryTestResult | null>(null);
  const [savedPolicyId, setSavedPolicyId] = useState(existing?.id || '');
  const [testOpen, setTestOpen] = useState(false);
  const [savingTest, setSavingTest] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const askTimer = useRef<number | null>(null);
  const readOnly = editorMode === 'view';

  const [policy, setPolicy] = useState<GovernancePolicy>(() => {
    if (existing) return fromRecord(existing, scope);
    const draft = emptyPolicy(scope, 'ROW_LEVEL');
    draft.generateId = true;
    draft.policyKey = newPolicyUuid();
    return draft;
  });

  useEffect(() => {
    logDataModels('app', 'governance_editor_opened', { mode, level, policyId: policyId ?? '', type: policy.type });
    return () => {
      if (askTimer.current) window.clearTimeout(askTimer.current);
    };
  }, [level, mode, policy.type, policyId]);

  useEffect(() => {
    setQueryUserId((current) => resolveDirectoryUserId(current, directory.simUsers));
  }, [directory.simUsers]);

  const path = buildPolicyPath({ space, model, table, policyKey: policy.policyKey });
  const documentMode = policy.type === 'ROW_LEVEL' || policy.type === 'COLUMN_LEVEL' ? policyDefinitionMode(policy) : complexity;
  const policyJson = useMemo(
    () => formatPolicyJson(policy, { path, assets: scopedAssets, complexity: documentMode }),
    [documentMode, path, policy, scopedAssets],
  );
  const title = editorMode === 'create' ? 'Create policy' : policy.name.trim() || 'Policy';

  const setName = (name: string) => {
    setPolicy((current) => {
      const nextKey = current.generateId || keyDirty ? current.policyKey : slugify(name);
      return { ...current, name, policyKey: nextKey };
    });
  };

  const changeType = (type: PolicyType) => {
    setPolicy((current) => ({
      ...current,
      type,
      rules: type === 'ACCESS_CONTROL' ? accessSeed() : [defaultRule(type)],
      advancedExpression: '',
    }));
    setQueryTestResult(null);
    logDataModels('app', 'governance_editor_type_changed', { type, level });
  };

  const openHelp = (source: 'header' | 'workbench') => {
    setHelpOpen(true);
    logDataModels('app', 'governance_expression_help_opened', { type: policy.type, path, source });
  };

  const insertHelpExpression = (expression: string) => {
    if (readOnly) return;
    setPolicy((current) => {
      if (current.type === 'ROW_LEVEL' || current.type === 'COLUMN_LEVEL') {
        return {
          ...current,
          rules: current.rules.map((item, index) => (
            index === 0 ? { ...item, definitionMode: 'complex', expression } : item
          )),
        };
      }
      return {
        ...current,
        advancedExpression: expression,
        rules: current.rules.map((item, index) => (index === 0 ? { ...item, expression } : item)),
      };
    });
    if (policy.type !== 'ROW_LEVEL' && policy.type !== 'COLUMN_LEVEL') setComplexity('complex');
    setHelpOpen(false);
    logDataModels('app', 'governance_expression_example_inserted', { type: policy.type, length: expression.length });
  };

  const runQueryTest = () => {
    const user = userById(queryUserId, directory.simUsers);
    if (!user) {
      logDataModels('warning', 'governance_query_tested', { ok: false, reason: 'no_user', userId: queryUserId, policyType: policy.type });
      showToast({ type: 'error', message: directory.loading ? 'Loading people…' : 'No people available to test as.' });
      return;
    }
    const result = testExpressionQuery(currentPolicyExpression(policy), scopedAssets, user);
    setQueryTestResult(result);
    if (policy.type !== 'ROW_LEVEL' && policy.type !== 'COLUMN_LEVEL') setComplexity('complex');
    logDataModels(result.ok ? 'app' : 'warning', 'governance_query_tested', {
      ok: result.ok,
      passed: result.passed ?? false,
      userId: queryUserId,
      policyType: policy.type,
    });
    showToast({
      type: !result.ok ? 'error' : result.passed ? 'success' : 'info',
      message: result.explanation,
    });
  };

  const setActivation = (status: PolicyStatus) => {
    setPolicy((current) => ({ ...current, status: detailStatusFromList(status) }));
  };

  const generateUuid = () => {
    const policyKey = newPolicyUuid();
    setPolicy((current) => ({ ...current, generateId: true, policyKey }));
    setKeyDirty(true);
    logDataModels('app', 'governance_editor_id_generate', { generateId: true, policyKey });
  };

  const persist = async (status: PolicyStatus, options?: { stay?: boolean }): Promise<GovernancePolicyRecord | undefined> => {
    const currentId = savedPolicyId || existing?.id || policy.id;
    const next: GovernancePolicy = {
      ...policy,
      id: currentId || policy.id,
      name: policy.name.trim(),
      description: policy.description?.trim(),
      policyKey: policy.policyKey.trim() || slugify(policy.name),
      status: detailStatusFromList(status),
    };
    next.document = toPolicyDocument(next, {
      path,
      assets: assetsWithColumns,
      complexity: next.type === 'ROW_LEVEL' || next.type === 'COLUMN_LEVEL' ? policyDefinitionMode(next) : complexity,
    });
    if (!next.name) {
      setErrors(['Policy name is required.']);
      logDataModels('warning', 'governance_editor_save_blocked', { reason: 'name' });
      return undefined;
    }
    if (isPolicyKeyTaken({ ...next, id: currentId || next.id })) {
      setErrors(['This policy ID is already used on this owner.']);
      logDataModels('warning', 'governance_editor_save_blocked', { reason: 'duplicate_key', policyKey: next.policyKey });
      return undefined;
    }
    if (status === 'Enabled') {
      if (columnsLoading) {
        setErrors(['Loading table columns…']);
        logDataModels('warning', 'governance_activate_blocked', { reason: 'columns_loading' });
        return undefined;
      }
      const issues = validatePolicyDraft(next, assetsWithColumns);
      if (issues.length) {
        setErrors(issues);
        logDataModels('warning', 'governance_activate_blocked', { errors: issues });
        return undefined;
      }
    }
    setErrors([]);
    try {
      const isCreate = !currentId;
      let record: GovernancePolicyRecord | null | undefined;
      if (status === 'Enabled') record = await activatePolicy(next);
      else if (status === 'Disabled') record = await deactivatePolicy(next);
      else if (currentId) record = await updatePolicyDraft({ ...next, id: currentId, status: 'DRAFT' });
      else record = await createPolicyDraft(next);
      const id = record?.id || currentId;
      if (id) {
        setSavedPolicyId(id);
        setPolicy((current) => ({ ...current, ...next, id }));
        if (isCreate) setEditorMode('edit');
      }
      if (isCreate) {
        showToast({
          type: record?.policyServerSynced ? 'success' : 'error',
          message: record?.policyServerSynced
            ? 'Policy created and synced to policy server'
            : 'Policy created. Policy server sync failed.',
        });
      } else {
        showToast({ type: 'success', message: status === 'Enabled' ? 'Policy enabled' : status === 'Disabled' ? 'Policy disabled' : 'Draft saved' });
      }
      if (!options?.stay) onClose();
      return record || (id ? { id } as GovernancePolicyRecord : undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save policy';
      setErrors([message]);
      logDataModels('error', 'governance_editor_save_failed', { message, status });
      showToast({ type: 'error', message });
      return undefined;
    }
  };

  const openPolicyTest = async () => {
    if (readOnly) {
      const id = savedPolicyId || existing?.id || policy.id;
      if (!id) {
        showToast({ type: 'error', message: 'Save the policy before testing.' });
        return;
      }
      setTestOpen(true);
      return;
    }
    setSavingTest(true);
    try {
      const record = await persist(listStatusFromDetail(policy.status), { stay: true });
      const id = record?.id || savedPolicyId || existing?.id || policy.id;
      if (!id) return;
      logDataModels('app', 'governance_policy_test_opened', { policyId: id, type: policy.type });
      setTestOpen(true);
    } finally {
      setSavingTest(false);
    }
  };

  const openAsk = () => {
    if (askTimer.current) window.clearTimeout(askTimer.current);
    setAskOpen(true);
    setAskAnalyzing(true);
    setAskSuggestions([]);
    logDataModels('app', 'governance_ask_opened', { type: policy.type, assets: scopedAssets.length });
    askTimer.current = window.setTimeout(() => {
      const result = suggestRules(policy, scopedAssets);
      const existingKeys = new Set(policy.rules.map(suggestionKey));
      const suggestions = result.suggestions.filter((item) => !existingKeys.has(suggestionKey(item.rule)));
      setAskAnalysis(result.analysis);
      setAskSuggestions(suggestions);
      setAskAnalyzing(false);
    }, 420);
  };

  const approveAsk = (suggestion: RuleSuggestion) => {
    setPolicy((current) => {
      if (current.type === 'ROW_LEVEL') {
        const first = current.rules[0] ?? defaultRule('ROW_LEVEL');
        const expression = suggestion.rule.expression ?? suggestion.expression;
        return {
          ...current,
          rules: [{
            ...first,
            ...suggestion.rule,
            id: first.id,
            condition: first.condition ?? suggestion.rule.condition,
            expression,
            definitionMode: expression?.trim() ? 'complex' : (first.definitionMode ?? 'simple'),
          }],
        };
      }
      const added = { ...suggestion.rule, id: `rule-ask-${Date.now()}` };
      if (current.type === 'COLUMN_LEVEL') {
        const expression = suggestion.rule.expression ?? suggestion.expression;
        return {
          ...current,
          rules: [...current.rules, {
            ...added,
            expression,
            definitionMode: expression?.trim() ? 'complex' : 'simple',
          }],
        };
      }
      return { ...current, rules: [...current.rules, added] };
    });
    setAskSuggestions((items) => items.filter((item) => item.id !== suggestion.id));
    if (suggestion.expression && policy.type !== 'ROW_LEVEL' && policy.type !== 'COLUMN_LEVEL') setComplexity('complex');
    logDataModels('app', 'governance_ask_approved', { title: suggestion.title, type: policy.type });
  };

  return (
    <section className={`relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${ui.body}`}>
      <header className={`shrink-0 border-b px-4 py-2 ${ui.border} ${ui.header}`}>
        <div className={`mb-2 flex flex-wrap items-center gap-1 text-xs ${muted}`}>
          <button type="button" className="hover:underline" onClick={onClose}>WorkSpace</button>
          <ChevronRight size={12} />
          <button type="button" className="hover:underline" onClick={onClose}>{space.name}</button>
          {model ? (
            <>
              <ChevronRight size={12} />
              <button type="button" className="hover:underline" onClick={onClose}>{model.name}</button>
            </>
          ) : null}
          {table ? (
            <>
              <ChevronRight size={12} />
              <button type="button" className="hover:underline" onClick={onClose}>{table.name}</button>
            </>
          ) : null}
          <ChevronRight size={12} />
          <button type="button" className="hover:underline" onClick={onClose}>Policies</button>
          <ChevronRight size={12} />
          <span className={ink}>{editorMode === 'create' ? 'Create policy' : editorMode === 'edit' ? 'Edit policy' : 'View policy'}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={`truncate text-base font-semibold ${ink}`}>{title}</h2>
            <div className="mt-0.5 flex min-w-0 items-center gap-1">
              <p className={`truncate font-mono text-[11px] ${muted}`}>{path}</p>
              <button
                type="button"
                className={`${iconBtn(isLight)} !h-5 !w-5`}
                title="Copy policy path"
                aria-label="Copy policy path"
                onClick={() => {
                  void navigator.clipboard?.writeText(path);
                  showToast({ type: 'success', message: 'Policy path copied' });
                  logDataModels('app', 'governance_policy_path_copied', { path });
                }}
              >
                <Copy size={12} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => void openPolicyTest()} disabled={savingTest}>
              <FlaskConical size={13} />
              {savingTest ? 'Saving…' : 'Test'}
            </button>
            <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => openHelp('header')}>
              <CircleHelp size={13} />
              Help
            </button>
            <button
              type="button"
              className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`}
              onClick={() => {
                setJsonOpen(true);
                logDataModels('app', 'governance_policy_json_opened', { type: policy.type, path, schema: POLICY_DOCUMENT_SCHEMA });
              }}
            >
              <FileText size={13} />
              Policy Schema
            </button>
            {editorMode === 'view' ? (
              <button type="button" className={primaryBtn('!h-7 !px-2.5 !text-xs')} onClick={() => setEditorMode('edit')}>Edit</button>
            ) : (
              <>
                <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={onClose}>Cancel</button>
                <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => persist('Draft')}>Save draft</button>
                <button type="button" className={primaryBtn('!h-7 !px-2.5 !text-xs')} onClick={() => persist(listStatusFromDetail(policy.status))}>Save</button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-[5vh]">
        <div className="w-full space-y-5">
          <Section title="Identity">
            <div className="w-full max-w-[50%] space-y-2">
              <div>
                <label className={`${labelClass(isLight)} !mb-1 !text-xs`}>Policy name</label>
                <input className={`${fieldClass(isLight)} !py-1.5 !text-xs`} disabled={readOnly} value={policy.name} onChange={(event) => setName(event.target.value)} placeholder="Region row filter" />
              </div>
              <div>
                <label className={`${labelClass(isLight)} !mb-1 !text-xs`}>Policy ID</label>
                <div className="flex items-center gap-2">
                  <input
                    className={`${fieldClass(isLight)} !py-1.5 !text-xs font-mono`}
                    disabled={readOnly}
                    value={policy.policyKey}
                    onChange={(event) => {
                      setKeyDirty(true);
                      setPolicy({ ...policy, generateId: false, policyKey: event.target.value.replace(/\s+/g, '_') });
                    }}
                    placeholder="region_row_filter"
                  />
                  <button
                    type="button"
                    className={iconBtn(isLight)}
                    title="Copy policy ID"
                    aria-label="Copy policy ID"
                    onClick={() => {
                      void navigator.clipboard?.writeText(policy.policyKey);
                      showToast({ type: 'success', message: 'Policy ID copied' });
                      logDataModels('app', 'governance_policy_id_copied', { policyKey: policy.policyKey });
                    }}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    type="button"
                    className={iconBtn(isLight)}
                    disabled={readOnly || editorMode !== 'create'}
                    title="Generate UUID"
                    aria-label="Generate UUID"
                    onClick={generateUuid}
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
              </div>
              <div>
                <label className={`${labelClass(isLight)} !mb-1 !text-xs`}>Description</label>
                <textarea
                  className={`${fieldClass(isLight)} min-h-[52px] !py-1.5 !text-xs resize-none`}
                  disabled={readOnly}
                  value={policy.description ?? ''}
                  onChange={(event) => setPolicy({ ...policy, description: event.target.value })}
                  placeholder="What this policy enforces and who it is for"
                />
              </div>
              <div>
                <label className={`${labelClass(isLight)} !mb-1 !text-xs`}>Policy type</label>
                <select className={`${fieldClass(isLight)} !py-1.5 !text-xs`} disabled={readOnly} value={policy.type} onChange={(event) => changeType(event.target.value as PolicyType)}>
                  {EDITOR_TYPES.map((type) => (
                    <option key={type} value={type}>{POLICY_TYPE_LABEL[type].replace(' Policy', '')}</option>
                  ))}
                  {policy.type === 'TIME_BASED' ? <option value="TIME_BASED">{POLICY_TYPE_LABEL.TIME_BASED.replace(' Policy', '')}</option> : null}
                </select>
              </div>
              <div>
                <label className={`${labelClass(isLight)} !mb-1 !text-xs`}>Activation</label>
                <div className={`flex rounded-lg border p-0.5 ${ui.border}`}>
                  {ACTIVATION.map((status) => (
                    <button
                      key={status}
                      type="button"
                      disabled={readOnly}
                      onClick={() => setActivation(status)}
                      className={`h-7 flex-1 rounded-md px-2 text-[11px] ${
                        listStatusFromDetail(policy.status) === status
                          ? isLight ? 'bg-[#F3F4F6] font-medium text-[#111827]' : 'bg-white/10 font-medium text-white'
                          : muted
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          <AppliesToSection policy={policy} onChange={setPolicy} assets={scopedAssets} isLight={isLight} complexity={complexity} readOnly={readOnly} />

          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold">Definition</h3>
              <div className="flex items-center gap-2">
                {!readOnly ? (
                  <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={openAsk}>
                    <Sparkles size={12} />
                    Ask
                  </button>
                ) : null}
                {policy.type !== 'ROW_LEVEL' && policy.type !== 'COLUMN_LEVEL' ? (
                  <div className={`flex rounded-lg border p-0.5 ${ui.border}`}>
                    {([
                      { id: 'simple', label: 'Classic' },
                      { id: 'complex', label: 'Advanced' },
                    ] as const).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setComplexity(item.id)}
                        className={`h-7 rounded-md px-2.5 text-[11px] ${
                          complexity === item.id
                            ? isLight ? 'bg-[#F3F4F6] font-medium text-[#111827]' : 'bg-white/10 font-medium text-white'
                            : muted
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <PolicyDefinitionFields
              policy={policy}
              onChange={setPolicy}
              assets={scopedAssets}
              isLight={isLight}
              complexity={complexity}
              readOnly={readOnly}
              workbench={{
                testUserId: queryUserId,
                onTestUserId: setQueryUserId,
                testResult: queryTestResult,
                onRunTest: () => { void openPolicyTest(); },
                onCopyNotice: (message) => showToast({ type: 'success', message }),
                onOpenHelp: () => openHelp('workbench'),
              }}
            />
          </section>

          {errors.length ? (
            <div className={`rounded-lg border px-3 py-2 text-[12px] ${isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'}`}>
              {errors.map((error) => <p key={error}>{error}</p>)}
            </div>
          ) : null}
        </div>
      </div>

      {askOpen ? (
        <AskOverlay
          isLight={isLight}
          analyzing={askAnalyzing}
          analysis={askAnalysis}
          suggestions={askSuggestions}
          onApprove={approveAsk}
          onReject={(suggestion) => {
            setAskSuggestions((items) => items.filter((item) => item.id !== suggestion.id));
            logDataModels('warning', 'governance_ask_rejected', { title: suggestion.title });
          }}
          onClose={() => {
            if (askTimer.current) window.clearTimeout(askTimer.current);
            setAskOpen(false);
          }}
        />
      ) : null}

      {helpOpen ? (
        <ExpressionHelpDialog
          isLight={isLight}
          policyType={policy.type}
          canInsert={!readOnly}
          onClose={() => setHelpOpen(false)}
          onInsert={insertHelpExpression}
          onCopy={(value) => {
            void navigator.clipboard?.writeText(value);
            showToast({ type: 'success', message: 'Copied to clipboard' });
          }}
        />
      ) : null}

      {testOpen ? (
        <PolicyTestDialog
          isLight={isLight}
          policy={policy}
          policyId={savedPolicyId || existing?.id || policy.id}
          assets={policy.type === 'ROW_LEVEL' ? assetsForRowFilter(policy, assetsWithColumns) : assetsWithColumns}
          initialUserId={queryUserId}
          onClose={() => setTestOpen(false)}
        />
      ) : null}

      {jsonOpen ? (
        <DialogShell
          title="Policy Schema"
          subtitle="Canonical policy schema. This is the policy."
          isLight={isLight}
          size="medium"
          onClose={() => setJsonOpen(false)}
          footer={(
            <>
              <button
                type="button"
                className={ghostBtn(isLight)}
                onClick={() => {
                  void navigator.clipboard?.writeText(policyJson);
                  logDataModels('app', 'governance_policy_json_copied', { type: policy.type, path, schema: POLICY_DOCUMENT_SCHEMA });
                  showToast({ type: 'success', message: 'Policy Schema copied' });
                }}
              >
                <Copy size={14} />
                Copy
              </button>
              <button type="button" className={primaryBtn()} onClick={() => setJsonOpen(false)}>Done</button>
            </>
          )}
        >
          <pre className={`max-h-[50vh] overflow-auto rounded-md px-3 py-3 font-mono text-[12px] leading-5 ${isLight ? 'bg-[#F7F8FA] text-[#1C1C1C]' : 'bg-white/5 text-slate-100'}`}>{policyJson}</pre>
        </DialogShell>
      ) : null}
    </section>
  );
}

function AskOverlay({
  isLight,
  analyzing,
  analysis,
  suggestions,
  onApprove,
  onReject,
  onClose,
}: {
  isLight: boolean;
  analyzing: boolean;
  analysis: string;
  suggestions: RuleSuggestion[];
  onApprove: (suggestion: RuleSuggestion) => void;
  onReject: (suggestion: RuleSuggestion) => void;
  onClose: () => void;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#111827]' : 'text-white';
  const line = isLight ? 'border-[#E5E7EB]' : 'border-white/10';
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center p-6">
      <button type="button" className="absolute inset-0 bg-[#0F172A]/35" aria-label="Close Ask AI" onClick={onClose} />
      <div className={`relative flex max-h-[min(640px,calc(100%-2rem))] w-[min(460px,100%)] flex-col overflow-hidden rounded-2xl border shadow-2xl ${isLight ? 'border-[#E5E7EB] bg-white' : 'border-white/10 bg-black'}`}>
        <header className={`flex items-start justify-between gap-3 border-b px-5 py-4 ${line}`}>
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={16} className={ink} />
              <h3 className={`text-[16px] font-semibold ${ink}`}>Ask AI</h3>
            </div>
            <p className={`mt-1 text-[12px] ${muted}`}>Review suggested rules, then approve or reject each one.</p>
          </div>
          <button type="button" className={iconBtn(isLight)} onClick={onClose} aria-label="Close Ask AI"><X size={16} /></button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {analyzing ? (
            <p className={`flex items-center gap-3 text-[13px] ${muted}`}><Sparkles size={16} className="animate-pulse" /> Analyzing assets and existing rules…</p>
          ) : (
            <>
              <p className={`mb-3 text-[13px] leading-5 ${ink}`}>{analysis}</p>
              {suggestions.length ? (
                <ul className="space-y-2.5">
                  {suggestions.map((suggestion) => (
                    <li key={suggestion.id} className={`rounded-xl border px-3 py-3 ${line}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-[13px] font-semibold ${ink}`}>{suggestion.title}</p>
                          <p className={`mt-1 text-[12px] ${muted}`}>{suggestion.reason}</p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button type="button" className={iconBtn(isLight)} aria-label={`Approve ${suggestion.title}`} onClick={() => onApprove(suggestion)}><CheckCircle2 size={16} /></button>
                          <button type="button" className={iconBtn(isLight)} aria-label={`Reject ${suggestion.title}`} onClick={() => onReject(suggestion)}><XCircle size={16} /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-[13px] ${muted}`}>No more suggestions.</p>
              )}
            </>
          )}
        </div>
        <footer className={`flex justify-end border-t px-5 py-3 ${line}`}>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
